import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import Modal from 'components/mui/Modal'
import Box from 'components/mui/Box'
import Typography from 'components/mui/Typography'
import Stack from 'components/mui/Stack'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import KycFatcaFieldsForm from 'components/forms/KycFatcaFieldsForm'
import { IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'
import { typography } from 'theme'
import {
  KYC_FATCA_DEFAULT_VALUES,
  buildKycFatcaFormDefaults
} from 'utils/kyc-fatca.utils'

/**
 * Small FATCA details modal shown after KYC complete.
 * Only editable PATCH fields: birthPlace, occupation, incomeSlab.
 */
const FatcaDetailsModal = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  defaultValues = null
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: KYC_FATCA_DEFAULT_VALUES
  })

  useEffect(() => {
    if (!open) {
      reset?.(KYC_FATCA_DEFAULT_VALUES)
      return
    }

    reset?.(buildKycFatcaFormDefaults?.(defaultValues ?? null) ?? { ...KYC_FATCA_DEFAULT_VALUES })
  }, [
    open,
    defaultValues?.birthPlace,
    defaultValues?.placeOfBirth,
    defaultValues?.occupation,
    defaultValues?.incomeSlab,
    defaultValues?.income,
    defaultValues?.annualIncome,
    reset
  ])

  const handleFormSubmit = (data) => {
    if (!data || loading) return
    onSubmit?.({
      birthPlace: data?.birthPlace?.trim?.() ?? '',
      occupation: data?.occupation ?? '',
      incomeSlab: data?.incomeSlab ?? ''
    })
  }

  const containerSx = {
    bgcolor: '#0B1120',
    borderRadius: '16px',
    p: { xs: 2.5, sm: 3 },
    position: 'relative',
    width: { xs: '100%', sm: 480, md: 520 },
    color: '#FFFFFF',
    boxShadow: '0px 16px 48px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.08)',
    maxHeight: '90vh',
    overflowY: 'auto'
  }

  return (
    <Modal
      open={!!open}
      onClose={onClose}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <Box sx={containerSx}>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close FATCA details"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#D0D6E5'
          }}
        >
          <Close />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 2.5, pr: 3 }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: typography?.fontWeight?.semibold,
              color: '#FFFFFF',
              mb: 0.5
            }}
          >
            FATCA Details
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              color: '#A3B1CC',
              lineHeight: 1.5
            }}
          >
            Please share a few details required for FATCA compliance.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit?.(handleFormSubmit)}
          noValidate
        >
          <Stack spacing={2.5}>
            <KycFatcaFieldsForm
              control={control}
              errors={errors}
              disabled={!!loading}
            />

            <AppButtonComponent
              type="submit"
              disabled={!isValid || !!loading}
              sx={{
                width: '100%',
                backgroundColor: '#4BB2F2',
                color: '#0B1120',
                fontWeight: typography?.fontWeight?.semibold,
                textTransform: 'none',
                borderRadius: '8px',
                height: 46,
                mt: 1,
                '&:hover': { backgroundColor: '#3aa2e5' },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(75, 178, 242, 0.4)',
                  color: '#0B1120'
                }
              }}
            >
              {loading ? 'Saving...' : 'Continue'}
            </AppButtonComponent>
          </Stack>
        </Box>
      </Box>
    </Modal>
  )
}

FatcaDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  defaultValues: PropTypes.shape({
    birthPlace: PropTypes.string,
    placeOfBirth: PropTypes.string,
    occupation: PropTypes.string,
    incomeSlab: PropTypes.string,
    income: PropTypes.string,
    annualIncome: PropTypes.string
  })
}

FatcaDetailsModal.defaultProps = {
  loading: false,
  defaultValues: null
}

export default FatcaDetailsModal
